import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Text,
  HStack,
  IconButton,
  Spinner,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react'
import { Check, X, ChevronDown } from 'lucide-react'

export interface SelectOption {
  value: string
  label: string
}

export interface EditableSelectFieldProps {
  label: string
  value: string | null | undefined
  fieldName: string
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  onUpdate: (fieldName: string, value: string) => Promise<void>
}

export function EditableSelectField({
  label,
  value,
  fieldName,
  options,
  placeholder = 'Non renseigné',
  disabled = false,
  onUpdate,
}: EditableSelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const selectRef = useRef<HTMLSelectElement>(null)

  // Sync editValue when value changes
  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  // Focus select when entering edit mode
  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isEditing])

  const handleEdit = () => {
    if (disabled) return
    setEditValue(value || '')
    setIsEditing(true)
  }

  const handleCancel = () => {
    setEditValue(value || '')
    setIsEditing(false)
  }

  const handleSubmit = async () => {
    if (editValue !== (value || '')) {
      setIsLoading(true)
      try {
        await onUpdate(fieldName, editValue)
      } catch (error) {
        console.error('Error updating field:', error)
        setEditValue(value || '') // Revert on error
      } finally {
        setIsLoading(false)
      }
    }
    setIsEditing(false)
  }

  // Get display label for current value
  const getDisplayLabel = () => {
    if (!value) return placeholder
    const option = options.find((o) => o.value === value)
    return option?.label || value
  }

  if (isEditing) {
    return (
      <Box>
        <Text fontSize="sm" color="gray.600" mb={1}>
          {label}
        </Text>
        <HStack gap={1}>
          <Box position="relative" flex={1}>
            <NativeSelectRoot>
              <NativeSelectField
                ref={selectRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                fontWeight="medium"
                pr={8}
              >
                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </NativeSelectField>
            </NativeSelectRoot>
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </Box>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <>
              <IconButton
                variant="ghost"
                size="xs"
                colorPalette="green"
                aria-label="Valider"
                onClick={handleSubmit}
              >
                <Check size={14} />
              </IconButton>
              <IconButton
                variant="ghost"
                size="xs"
                colorPalette="red"
                aria-label="Annuler"
                onClick={handleCancel}
              >
                <X size={14} />
              </IconButton>
            </>
          )}
        </HStack>
      </Box>
    )
  }

  return (
    <Box>
      <Text fontSize="sm" color="gray.600" mb={1}>
        {label}
      </Text>
      <Text
        fontWeight="medium"
        color={value ? 'inherit' : 'gray.400'}
        px={2}
        py={1}
        borderRadius="md"
        _hover={disabled ? undefined : { bg: 'gray.50' }}
        cursor={disabled ? 'default' : 'pointer'}
        onClick={handleEdit}
      >
        {getDisplayLabel()}
      </Text>
    </Box>
  )
}
